<template>
  <div class="clientes-wrapper">
    <ModalLoading :isLoading="isLoading" />

    <div class="opciones">
      <div class="primary-buttons">
        <button
          id="btnAdd"
          class="btn btn-primary"
          @click="openModal"
          style="width: 200px; white-space: nowrap"
        >
          Agregar Clientes
        </button>

        <ExportButton
          :columns="columns"
          :rows="rows"
          fileName="Clientes.pdf"
          class="export-button"
        />
      </div>

      <div class="search-bar">
        <input
          class="busqueda"
          type="text"
          v-model="searchQuery"
          placeholder="Buscar cliente..."
        />
      </div>
    </div>

    <div class="table-container">
      <!-- Indicador de carga -->
      <div v-if="isLoading" class="loading-indicator">Cargando clientes...</div>

      <!-- Mensaje si no hay datos -->
      <div v-else-if="paginatedClientes.length === 0" class="no-data">
        No se encontraron clientes para mostrar.
      </div>

      <table v-else class="table">
        <thead>
          <tr>
            <th>#</th>
            <th>Nombre</th>
            <th>Tipo</th>
            <th>Correo</th>
            <th>Dirección</th>
            <th>Teléfono</th>
            <th>RTN</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(cliente, index) in paginatedClientes" :key="index">
            <td data-label="#">
              {{ (currentPage - 1) * pageSize + index + 1 }}
            </td>
            <td data-label="Nombre">
              {{ cliente.nombre_completo }}
              <span v-if="esTerceraEdad(cliente.fecha_nacimiento)" class="badge-3edad" title="Tercera edad (65+)">3a Edad</span>
            </td>
            <td data-label="Tipo">
              <span class="tipo-badge" :class="'tipo-' + (cliente.tipo_cliente || 'normal').toLowerCase()">
                {{ cliente.tipo_cliente || 'Normal' }}
                <span v-if="Number(cliente.descuento_habitual) > 0"> -{{ cliente.descuento_habitual }}%</span>
              </span>
            </td>
            <td data-label="Correo">{{ cliente.correo }}</td>
            <td data-label="Dirección">{{ cliente.direccion }}</td>
            <td data-label="Teléfono">{{ cliente.telefono }}</td>
            <td data-label="RTN">{{ cliente.rtn }}</td>
            <td data-label="Acciones">
              <button
                class="accion-btn btn-ver"
                @click="verDetalle(cliente)"
                title="Ver información del cliente"
              >
                <i class="bi bi-eye-fill"></i>
              </button>
              <button
                class="accion-btn btn-historial"
                @click="verHistorial(cliente)"
                title="Ver historial de compras"
              >
                <i class="bi bi-clock-history"></i>
              </button>
              <button
                v-if="puedeEditar"
                class="accion-btn btn-editar"
                @click="editCliente(cliente)"
                title="Editar cliente"
              >
                <i class="bi bi-pencil-fill"></i>
              </button>
              <button
                v-if="puedeEditar"
                class="accion-btn btn-eliminar"
                @click="deleteCliente(cliente)"
                title="Eliminar cliente"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación -->
      <div class="pagination-wrapper">
        <div class="pagination-info">
          Mostrando {{ (currentPage - 1) * pageSize + 1 }} a
          {{ Math.min(currentPage * pageSize, filteredClientes.length) }}
          de {{ filteredClientes.length }} registros
        </div>
        <div class="pagination-container">
          <button
            class="pagination-button"
            :disabled="currentPage === 1"
            @click="previousPage"
          >
            Anterior
          </button>
          <button
            class="pagination-button"
            :disabled="currentPage === totalPages"
            @click="nextPage"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>

    <div class="modal" v-if="showConfirmModal">
      <div class="modal-confirm">
        <div class="modal-header">
          <h2>Confirmación</h2>
        </div>
        <div class="modal-body-confirm">
          <p>¿Estás seguro de que quieres eliminar este cliente?</p>
        </div>
        <div class="modal-footer">
          <div class="action-buttons">
            <btnGuardarModal
              texto="Sí, eliminar"
              style="background-color: red"
              @click="deleteCliente()"
            />
            <btnCerrarModal texto="No, regresar" @click="cancelDelete" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="isModalOpen" class="modal">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="h2-modal-content">
            {{ isEditing ? "Editar Cliente" : "Agregar Cliente" }}
          </h2>
        </div>

        <div class="modal-body">
          <!-- Nombre — ancho completo -->
          <div class="form-group">
            <label>Nombre:</label>
            <input
              ref="nombreInput"
              v-model="clienteForm.nombre_completo"
              type="text"
              required
            />
          </div>

          <!-- Correo | Dirección -->
          <div class="form-row-2col">
            <div class="form-group">
              <label>Correo:</label>
              <input v-model="clienteForm.correo" type="email" />
            </div>
            <div class="form-group">
              <label>Dirección:</label>
              <input v-model="clienteForm.direccion" type="text" />
            </div>
          </div>

          <!-- Teléfono — ancho completo (tiene selector de país) -->
          <div class="form-group">
            <label>Teléfono:</label>
            <div class="phone-input-container">
              <select
                v-model="selectedCountry"
                @change="updatePhoneValidation"
                class="select-phone"
              >
                <option value="">País</option>
                <option
                  v-for="(country, code) in countryData"
                  :key="code"
                  :value="code"
                >
                  {{ country.emoji }} {{ country.code }}
                </option>
              </select>
              <input
                v-model="clienteForm.telefono"
                type="text"
                class="input-phone"
                :placeholder="'Número (' + phoneLength + ' dígitos)'"
                required
              />
            </div>
          </div>

          <!-- RTN | Tipo de cliente -->
          <div class="form-row-2col">
            <div class="form-group">
              <label>RTN: <span style="font-weight:400;color:#999">(opcional, 14 díg.)</span></label>
              <input
                v-model="clienteForm.rtn"
                type="text"
                maxlength="14"
                placeholder="00000000000000"
              />
            </div>
            <div class="form-group">
              <label>Tipo de cliente:</label>
              <select v-model="clienteForm.tipo_cliente" class="select-tipo-cliente">
                <option value="Normal">Normal</option>
                <option value="Frecuente">Frecuente</option>
                <option value="VIP">VIP</option>
              </select>
            </div>
          </div>

          <!-- Descuento habitual | Fecha de nacimiento -->
          <div class="form-row-2col">
            <div class="form-group">
              <label>Descuento habitual: <span style="font-weight:400;color:#999">(% en ventas)</span></label>
              <input
                v-model.number="clienteForm.descuento_habitual"
                type="number"
                min="0"
                max="100"
                step="0.5"
                placeholder="0"
              />
            </div>
            <div class="form-group">
              <label>
                Fecha de nacimiento:
                <span v-if="clienteForm.fecha_nacimiento && esTerceraEdad(clienteForm.fecha_nacimiento)" class="badge-3edad" style="margin-left:6px">3a Edad</span>
              </label>
              <input
                v-model="clienteForm.fecha_nacimiento"
                type="date"
                :max="hoy"
              />
              <span v-if="clienteForm.fecha_nacimiento && !esTerceraEdad(clienteForm.fecha_nacimiento)" class="hint-edad">
                Edad: {{ calcularEdadTexto(clienteForm.fecha_nacimiento) }}
              </span>
            </div>
          </div>

          <!-- Número de identidad — ancho completo -->
          <div class="form-group">
            <label>Número de identidad: <span style="font-weight:400;color:#999">(opcional)</span></label>
            <input
              v-model="clienteForm.numero_identidad"
              type="text"
              placeholder="Ej. 0801-1990-12345"
            />
          </div>

          <!-- Sección crédito -->
          <div class="form-section-divider">
            <span>Configuración de Crédito <small style="font-weight:400;color:#999">(opcional)</small></span>
          </div>

          <div class="form-row-2col">
            <div class="form-group">
              <label>Límite de crédito (L.):</label>
              <input
                v-model.number="clienteForm.limite_credito"
                type="number"
                min="0"
                step="0.01"
                placeholder="Sin límite"
              />
            </div>
            <div class="form-group">
              <label>Días de crédito:</label>
              <input
                v-model.number="clienteForm.dias_credito"
                type="number"
                min="0"
                step="1"
                placeholder="Sin vencimiento"
              />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="action-buttons">
            <btnGuardarModal
              :texto="isEditing ? 'Guardar Cambios' : 'Agregar Cliente'"
              @click="guardarCliente"
            >
            </btnGuardarModal>
            <btnCerrarModal
              :texto="'Cerrar'"
              @click="closeModal"
            ></btnCerrarModal>
          </div>
        </div>
      </div>
    </div>
    <!-- Modal historial de compras -->
    <HistorialClienteModal
      :is-visible="showHistorial"
      :cliente="clienteHistorial"
      @close="showHistorial = false"
    />

    <!-- Modal detalle cliente -->
    <div v-if="showDetalle && clienteDetalle" class="modal">
      <div class="modal-detalle">
        <div class="detalle-header">
          <div class="detalle-avatar">{{ iniciales(clienteDetalle.nombre_completo) }}</div>
          <div class="detalle-titulo">
            <h2>{{ clienteDetalle.nombre_completo }}
              <span v-if="esTerceraEdad(clienteDetalle.fecha_nacimiento)" class="badge-3edad">3a Edad</span>
            </h2>
            <span class="tipo-badge" :class="'tipo-' + (clienteDetalle.tipo_cliente || 'normal').toLowerCase()">
              {{ clienteDetalle.tipo_cliente || 'Normal' }}
              <span v-if="Number(clienteDetalle.descuento_habitual) > 0"> · {{ clienteDetalle.descuento_habitual }}% desc.</span>
            </span>
          </div>
          <button class="modal-close-x" @click="showDetalle = false">✕</button>
        </div>

        <div class="detalle-body">
          <div class="detalle-grid">
            <div class="detalle-item">
              <span class="detalle-label"><i class="bi bi-hash"></i> ID Cliente</span>
              <span class="detalle-valor">#{{ clienteDetalle.id_cliente }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label"><i class="bi bi-envelope"></i> Correo</span>
              <span class="detalle-valor">{{ clienteDetalle.correo || '—' }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label"><i class="bi bi-telephone"></i> Teléfono</span>
              <span class="detalle-valor">{{ clienteDetalle.telefono || '—' }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label"><i class="bi bi-geo-alt"></i> Dirección</span>
              <span class="detalle-valor">{{ clienteDetalle.direccion || '—' }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label"><i class="bi bi-card-text"></i> RTN</span>
              <span class="detalle-valor">{{ clienteDetalle.rtn || '—' }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label"><i class="bi bi-person-vcard"></i> N° Identidad</span>
              <span class="detalle-valor">{{ clienteDetalle.numero_identidad || '—' }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label"><i class="bi bi-cake2"></i> Fecha nacimiento</span>
              <span class="detalle-valor">
                {{ clienteDetalle.fecha_nacimiento ? formatFecha(clienteDetalle.fecha_nacimiento) : '—' }}
                <span v-if="clienteDetalle.fecha_nacimiento" class="hint-edad" style="display:inline;margin-left:6px">
                  ({{ calcularEdadTexto(clienteDetalle.fecha_nacimiento) }})
                </span>
              </span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label"><i class="bi bi-percent"></i> Descuento habitual</span>
              <span class="detalle-valor">{{ clienteDetalle.descuento_habitual || 0 }}%</span>
            </div>
            <div v-if="clienteDetalle.limite_credito || clienteDetalle.dias_credito" class="detalle-item detalle-credito-item">
              <span class="detalle-label"><i class="bi bi-credit-card"></i> Límite de crédito</span>
              <span class="detalle-valor">
                <span v-if="clienteDetalle.limite_credito">L. {{ Number(clienteDetalle.limite_credito).toFixed(2) }}</span>
                <span v-else>Sin límite</span>
                <span v-if="clienteDetalle.dias_credito" style="margin-left:8px;color:#666;font-size:12px">· {{ clienteDetalle.dias_credito }} días</span>
              </span>
            </div>
          </div>
        </div>

        <div class="detalle-footer">
          <button v-if="puedeEditar" class="btn btn-warning" @click="editClienteDesdeDetalle(clienteDetalle)">
            <i class="bi bi-pencil-fill"></i> Editar
          </button>
          <button class="btn btn-historial" @click="verHistorialDesdeDetalle(clienteDetalle)">
            <i class="bi bi-clock-history"></i> Ver historial
          </button>
          <button class="btn btn-secondary" @click="showDetalle = false">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ExportButton from "../components/ExportButton.vue";
import btnGuardarModal from "../components/botones/modales/btnGuardar.vue";
import btnCerrarModal from "../components/botones/modales/btnCerrar.vue";
import HistorialClienteModal from "../components/HistorialClienteModal.vue";
import solicitudes from "../../services/solicitudes";
import { validacionesClientes } from "../../services/validarCampos.js";
import { notis } from "../../services/notificaciones.js";
const {
  getClientesbyEmpresa,
  postCliente,
  patchCliente,
  desactivarCliente,
} = require("../../services/clienteSolicitudes.js");
const { esAdminOCeo } = require("../../services/usuariosSolicitudes");
import { COUNTRY_CODES } from "../../services/countrySelector.js";
import ModalLoading from "@/components/ModalLoading.vue";
import { setPageTitle } from "@/components/pageMetadata";
const { getSocket } = require('@/services/socket');

export default {
  components: {
    ExportButton,
    btnGuardarModal,
    btnCerrarModal,
    ModalLoading,
    HistorialClienteModal,
  },
  data() {
    return {
      titulo: "Clientes",
      isLoading: false,
      showConfirmModal: false,
      clienteToDelete: null,
      searchQuery: "",
      isModalOpen: false,
      isEditing: false,
      editIndex: null,
      id_usuario: 0,
      puedeEditar: false,
      selectedCountry: "HN",
      countryData: COUNTRY_CODES,
      phoneLength: 8,
      currentPage: 1,
      pageSize: 10,
      clienteForm: {
        id_cliente: null,
        nombre_completo: "",
        correo: "",
        direccion: "",
        telefono: "",
        rtn: "",
        tipo_cliente: "Normal",
        descuento_habitual: 0,
        fecha_nacimiento: "",
        numero_identidad: "",
        limite_credito: null,
        dias_credito: null,
      },
      clientes: [],
      showHistorial: false,
      clienteHistorial: null,
      showDetalle: false,
      clienteDetalle: null,
    };
  },
  async mounted() {
    this.isLoading = true;
    setPageTitle("Clientes");
    try {
      this.id_usuario = await solicitudes.solicitarUsuarioToken();
      this.clientes = await getClientesbyEmpresa(this.id_usuario);
      this.puedeEditar = await esAdminOCeo(this.id_usuario);
    } catch {
      // ignore — isLoading siempre se limpia en finally
    } finally {
      this.isLoading = false;
    }
    const _s = getSocket();
    if (_s) {
      this._wsRefresh = async () => { if (this.id_usuario) this.clientes = await getClientesbyEmpresa(this.id_usuario); };
      _s.on('clientes:refresh', this._wsRefresh);
    }
  },
  beforeUnmount() {
    const _s = getSocket();
    if (_s && this._wsRefresh) _s.off('clientes:refresh', this._wsRefresh);
  },
  computed: {
    hoy() {
      return new Date().toISOString().split('T')[0];
    },
    filteredClientes() {
      return [...this.clientes]
        .reverse()
        .filter(
          (cliente) =>
            cliente.nombre_completo
              .toLowerCase()
              .includes(this.searchQuery.toLowerCase()) ||
            cliente.telefono.includes(this.searchQuery)
        );
    },
    paginatedClientes() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.filteredClientes.slice(startIndex, endIndex);
    },
    totalPages() {
      return Math.ceil(this.filteredClientes.length / this.pageSize);
    },
  },
  methods: {
    esTerceraEdad(fechaNacimiento) {
      if (!fechaNacimiento) return false;
      const hoy = new Date();
      const nac = new Date(fechaNacimiento);
      return (hoy.getFullYear() - nac.getFullYear() - (hoy < new Date(hoy.getFullYear(), nac.getMonth(), nac.getDate()) ? 1 : 0)) >= 65;
    },

    calcularEdadTexto(fechaNacimiento) {
      if (!fechaNacimiento) return '';
      const hoy = new Date();
      const nac = new Date(fechaNacimiento);
      const edad = hoy.getFullYear() - nac.getFullYear() - (hoy < new Date(hoy.getFullYear(), nac.getMonth(), nac.getDate()) ? 1 : 0);
      return `${edad} años`;
    },

    updatePhoneValidation() {
      if (this.selectedCountry && this.countryData[this.selectedCountry]) {
        this.phoneLength = this.countryData[this.selectedCountry].length;
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    openModal() {
      this.isModalOpen = true;
      this.$nextTick(() => {
        this.$refs.nombreInput?.focus();
      });
    },

    closeModal() {
      this.isModalOpen = false;
      this.clearForm();
    },

    clearForm() {
      this.clienteForm = {
        id_cliente: null,
        nombre_completo: "",
        correo: "",
        direccion: "",
        telefono: "",
        rtn: "",
        tipo_cliente: "Normal",
        descuento_habitual: 0,
        fecha_nacimiento: "",
        numero_identidad: "",
        limite_credito: null,
        dias_credito: null,
      };
      this.isEditing = false;
      this.editIndex = null;
    },

    async guardarCliente() {
      if (
        !validacionesClientes.validarCampos(
          this.clienteForm,
          this.selectedCountry
        )
      ) {
        return;
      }
      this.isLoading = true;

      this.clienteForm.id_usuario = this.id_usuario;
      let response;

      if (this.isEditing) {
        const nuevoRegistro = await patchCliente(
          this.clienteForm,
          this.clientes[this.editIndex].id_cliente
        );
        if (nuevoRegistro == true) {
          this.isLoading = false;
          notis("success", "Actualizado datos del cliente...");
          Object.assign(this.clientes[this.editIndex], this.clienteForm);
        }
      } else {
        try {
          response = await postCliente(this.clienteForm, this.id_usuario);
          if (response && response.id_cliente) {
            this.isLoading = false;
            notis("success", "Cliente guardado correctamente");
            this.clientes.push(response);
          } else {
            throw new Error("Error al crear el cliente.");
          }
        } catch {
          // error ya manejado por el bloque interno
        }
      }
      this.closeModal();
    },

    editCliente(cliente) {
      this.clienteForm = { ...cliente };
      this.isEditing = true;
      this.editIndex = this.clientes.findIndex(
        (item) => item.id_cliente === cliente.id_cliente
      );
      this.openModal();
      this.$nextTick(() => {
        this.$refs.nombreInput?.focus();
      });
    },

    async deleteCliente(cliente) {
      if (!this.showConfirmModal) {
        this.clienteToDelete = cliente;
        this.showConfirmModal = true;
        return;
      }

      this.isLoading = true;
      try {
        const response = await desactivarCliente(
          this.clienteToDelete.id_cliente
        );
        if (response === true) {
          this.clientes = this.clientes.filter(
            (item) => item.id_cliente !== this.clienteToDelete.id_cliente
          );
          this.isLoading = false;
          notis("success", "Cliente eliminado correctamente");
        } else {
          throw response;
        }
      } catch (error) {
        notis("error", error.message);
      } finally {
        this.showConfirmModal = false;
        this.clienteToDelete = null;
        this.isLoading = false;
      }
    },

    cancelDelete() {
      this.showConfirmModal = false;
      this.clienteToDelete = null;
    },

    verDetalle(cliente) {
      this.clienteDetalle = cliente;
      this.showDetalle = true;
    },

    editClienteDesdeDetalle(cliente) {
      this.showDetalle = false;
      this.$nextTick(() => this.editCliente(cliente));
    },

    verHistorialDesdeDetalle(cliente) {
      this.showDetalle = false;
      this.$nextTick(() => this.verHistorial(cliente));
    },

    verHistorial(cliente) {
      this.clienteHistorial = cliente;
      this.showHistorial = true;
    },

    iniciales(nombre) {
      if (!nombre) return '?';
      return nombre.trim().split(/\s+/).slice(0, 2).map(p => p[0].toUpperCase()).join('');
    },

    formatFecha(fecha) {
      if (!fecha) return '—';
      const [y, m, d] = fecha.split('T')[0].split('-');
      return `${d}/${m}/${y}`;
    },

    changeFavicon(iconPath) {
      const link =
        document.querySelector("link[rel*='icon']") ||
        document.createElement("link");
      link.type = "image/x-icon";
      link.rel = "icon";
      link.href = iconPath;
      document.getElementsByTagName("head")[0].appendChild(link);
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");

/* Fuente global */
* {
  font-family: "Montserrat", sans-serif;
  box-sizing: border-box;
}

.accion-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 15px;
  color: #fff;
  margin: 0 2px;
  transition: opacity .15s, transform .1s;
}
.accion-btn:hover { opacity: .85; transform: scale(1.08); }

.btn-ver      { background: #0e7490; }
.btn-historial{ background: #3b82f6; }
.btn-editar   { background: #f59e0b; color: #111; }
.btn-eliminar { background: #ef4444; }

/* ── Modal detalle ─────────────────────────────────── */
.modal-detalle {
  background: #fff;
  border-radius: 14px;
  width: 90%;
  max-width: 520px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  overflow: hidden;
}
.dark .modal-detalle { background: #1e1e1e; color: #e5e7eb; }

.detalle-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  position: relative;
}
.dark .detalle-header { background: #111827; border-color: #374151; }

.detalle-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #c09d62;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.detalle-titulo { flex: 1; }
.detalle-titulo h2 {
  margin: 0 0 4px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #111;
}
.dark .detalle-titulo h2 { color: #f3f4f6; }

.modal-close-x {
  position: absolute;
  top: 12px;
  right: 14px;
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
}
.modal-close-x:hover { color: #111; }
.dark .modal-close-x:hover { color: #fff; }

.detalle-body { padding: 20px 24px; overflow-y: auto; flex: 1; }

.detalle-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 20px;
}

.detalle-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detalle-label {
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: .04em;
}
.detalle-label i { margin-right: 4px; }

.detalle-valor {
  font-size: 14px;
  color: #111827;
  font-weight: 500;
  word-break: break-word;
}
.dark .detalle-valor { color: #e5e7eb; }

.detalle-footer {
  display: flex;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  justify-content: flex-end;
}
.dark .detalle-footer { background: #111827; border-color: #374151; }

.btn-secondary {
  background: #6b7280;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 13px;
}
.btn-secondary:hover { background: #4b5563; }

@media (max-width: 480px) {
  .detalle-grid { grid-template-columns: 1fr; }
  .modal-detalle { max-width: 95%; }
}

.tipo-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}
.tipo-normal    { background: #f3f4f6; color: #6b7280; }
.tipo-frecuente { background: #dbeafe; color: #1d4ed8; }
.tipo-vip       { background: #fef3c7; color: #92400e; }

.badge-3edad {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 700;
  background: #e0f7e9;
  color: #1a7f3c;
  border-radius: 4px;
  vertical-align: middle;
}
.dark .badge-3edad { background: rgba(26,127,60,0.25); color: #4ade80; }

.hint-edad {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #888;
}
.dark .hint-edad { color: #aaa; }

.dark .tipo-normal    { background: rgba(107,114,128,0.2); color: #9ca3af; }
.dark .tipo-frecuente { background: rgba(29,78,216,0.25);  color: #93c5fd; }
.dark .tipo-vip       { background: rgba(146,64,14,0.25);  color: #fcd34d; }

.select-tipo-cliente {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
  color: #333;
}
.dark .select-tipo-cliente {
  background: #2d2d2d;
  border-color: #555;
  color: #eee;
}

/* Opciones y búsqueda */
.opciones {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
}

#btnAdd,
.export-button {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  padding: 0 20px;
  white-space: nowrap;
  margin: 0;
}

.busqueda {
  width: 100%;
  height: 40px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid #ddd;
  font-size: 14px;
}

input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: #c09d62;
  box-shadow: 0 0 0 3px rgba(192, 157, 98, 0.2);
  transition: all 0.3s ease;
}

.registros {
  height: 100%;
  padding-bottom: 1%;
  flex-grow: 1;
  min-width: 200px;
}

/* Botones */

#btnAggCli {
  background-color: #a38655;
  border-radius: 8px;
}

#btnCerrarM {
  margin-left: 14rem;
  border-radius: 8px;
}

#btnAdd {
  background-color: #c09d62;
  font-size: 16px;
  width: auto;
  min-width: 170px;
  height: 40px;
  border-radius: 10px;
  color: black;
  font-weight: bold;
  white-space: nowrap;
}

#btnAdd:hover {
  background-color: #a38655;
  transform: scale(1.05);
  transition: all 0.3s ease;
}

.export-button {
  margin: 0;
}


#AddClienteModal,
#BtnCerrar {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  color: #fff;
  cursor: pointer;
  margin-right: 1rem;
}

#AddClienteModal {
  background-color: #007bff;
}

#BtnCerrar {
  background-color: rgb(93, 100, 104);
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f8f9fa;
}

.modal-confirm {
  background: white;
  border-radius: 12px;
  width: 25%;
  max-width: 1000px;
  max-height: 90vh;
  padding: 0;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  margin: 20px;
  overflow: hidden;
}

.modal-header h2 {
  margin: 0;
  color: #333;
  font-size: 1.5rem;
  font-weight: 600;
}

.modal-body-confirm {
  padding: 24px;
  overflow-y: auto;
  background-color: white;
}

.dark .modal-body-confirm {
  background-color: #333;
}

/* Form Layout */
.form-row {
  margin-bottom: 20px;
}

.form-columns {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 20px;
}

.full-width {
  grid-column: 1 / -1;
}

.modal-body {
  margin-top: 15px;
}

/* Form Groups */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background-color: white;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #c09d62;
  box-shadow: 0 0 0 3px rgba(192, 157, 98, 0.2);
}

.form-group textarea {
  min-height: 100px;
  resize: vertical;
}

/* Separador de sección en formulario */
.form-section-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0 16px;
  font-weight: 600;
  font-size: 13px;
  color: #555;
}
.form-section-divider::before,
.form-section-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e0e0e0;
}

/* Grid 2 columnas para campos de crédito */
.form-row-2col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
@media (max-width: 480px) {
  .form-row-2col { grid-template-columns: 1fr; }
}

/* Footer */
.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

/* Dark Mode */
.dark .modal-categoria {
  background-color: #2d2d2d;
}

.dark .modal-header,
.dark .modal-footer {
  background-color: #1e1e1e;
  border-color: #404040;
}

.dark .modal-header h2 {
  color: #fff;
}

.dark .form-group label {
  color: #fff;
}

.dark .form-group input,
.dark .form-group textarea,
.dark .form-group select {
  background-color: #383838;
  border-color: #404040;
  color: #fff;
}

/* Modal */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modalShowConfirm-Si {
  background-color: #dc3545;
  color: white;
}

.modalShowConfirm-no {
  background-color: #6c757d;
  color: white;
}

.modal-content {
  background-color: white;
  padding: 24px 28px;
  border-radius: 10px;
  width: 95%;
  max-width: 820px;
  margin: 20px;
}

.h2-modal-content {
  margin-top: 0px;
}

/* Tabla */
.clientes-wrapper {
  padding: 16px;
  width: 100%;
  overflow-x: hidden;
}

.table-container {
  width: 100%;
  border-radius: 10px;
  border: 1px solid #ddd;
  margin-top: 16px;
  overflow-x: auto;
}

.table {
  width: 100%;
  min-width: 800px;
  border-collapse: separate;
  border-spacing: 0;
}

.table th,
.table td {
  padding: 8px;
}

.table thead {
  background-color: white;
}

.table th,
.table td {
  padding: 8px;
  text-align: center;
}

.table thead th {
  border-bottom: 1px solid #ddd;
}

.table tbody td {
  border-top: 1px solid #ddd;
}

.table thead th:first-child {
  border-top-left-radius: 10px;
}

.table thead th:last-child {
  border-top-right-radius: 10px;
}

.table tbody tr:last-child td:first-child {
  border-bottom-left-radius: 10px;
}

.table tbody tr:last-child td:last-child {
  border-bottom-right-radius: 10px;
}

/* Formulario */
.form-group {
  margin-bottom: 16px;
  width: 100%;
}

.form-group label {
  display: flex;
  margin-bottom: 8px;
}

.form-group input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

#form-tel {
  width: 100%;
  max-width: 300px;
}

/* Selección personalizada */
.custom-select {
  border: 1px solid #ccc;
  border-radius: 5px;
  height: 35px;
  font-size: 16px;
  padding: 5px;
  background-color: #fff;
  cursor: pointer;
  width: 80px;
}

.custom-select:focus {
  outline: none;
  border-color: #a38655;
}

.custom-select option {
  font-size: 16px;
}

/* Estilos generales */
.btn {
  padding: 8px 16px;
  margin: 4px;
  border: none;
  cursor: pointer;
}

.primary-buttons {
  display: flex;
  gap: 10px;
  flex-shrink: 0; /* Evita que los botones se encogan */
}

.search-bar {
  flex-grow: 1; /* Ocupa el espacio restante */
  max-width: 250px; /* Limita el ancho máximo */
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.rol {
  color: #969696;
  font-size: 14px;
}

#campana {
  margin-right: 10px;
  font-size: 18px;
  color: #a38655;
}

.container-top {
  width: 100%;
  text-align: right;
}

.phone-input-container {
  display: flex;
  gap: 8px;
}

.phone-input-container select {
  width: 110px;
  margin: 0;
  /* Importante: sin márgenes */
  height: 35px;
  padding: 0 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.phone-input-container input {
  flex: 1;
  height: 35px;
  padding: 0 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

/* Estilos para modo oscuro */
.dark .country-code-select,
.dark .phone-input {
  background-color: #383838;
  border-color: #404040;
  color: #fff;
}

/* Media Queries */
@media screen and (max-width: 1024px) {
  .modal-content {
    width: 80%;
    max-width: 700px;
  }

  .modal-confirm {
    width: 40%;
  }

  .phone-input-container {
    flex-direction: row;
  }
}

@media screen and (max-width: 768px) {
  .opciones {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .primary-buttons {
    display: flex;
    gap: 10px;
    width: 100%;
  }

  .search-bar {
    grid-column: 1;
  }

  .table-container {
    margin-top: 12px;
    border-radius: 8px;
  }

  .table {
    min-width: 650px;
  }

  .modal-content {
    width: 90%;
  }

  .modal-confirm {
    width: 80%;
  }

  .form-group {
    margin-bottom: 12px;
  }

  .pagination-wrapper {
    flex-direction: column;
    gap: 12px;
    padding: 12px;
  }

  .opciones {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .primary-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .search-bar {
    max-width: none;
  }
}

@media screen and (max-width: 480px) {
  /* Convertir tabla en tarjetas */
  .table {
    display: block;
    min-width: unset;
    border: none;
  }

  .primary-buttons {
    flex-direction: column;
  }

  .table thead {
    display: none;
  }

  .table tbody {
    display: block;
  }

  .table tr {
    display: block;
    margin-bottom: 12px;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: #fff;
  }

  .table td {
    display: grid;
    grid-template-columns: 40% 60%;
    padding: 8px 4px;
    text-align: left;
    border: none;
    font-size: 14px;
  }

  .table td::before {
    content: attr(data-label);
    font-weight: bold;
    color: #666;
  }

  /* Modo oscuro para tarjetas */
  .dark .table tr {
    background: #2d2d2d;
    border-color: #404040;
  }

  .dark .table td::before {
    color: #aaa;
  }

  /* Columna de acciones */
  .table td:last-child {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 12px;
    margin-top: 8px;
    border-top: 1px solid #eee;
    grid-column: 1 / -1;
  }

  .dark .table td:last-child {
    border-top-color: #404040;
  }

  /* Ajustes de botones */
  .accion-btn {
    width: 32px;
    height: 32px;
    margin: 0;
    font-size: 13px;
  }

  /* Ajustes de input teléfono */
  .phone-input-container {
    flex-direction: column;
    gap: 8px;
  }

  .phone-input-container select,
  .phone-input-container input {
    width: 100%;
  }

  /* Ajustes modales */
  .modal-content,
  .modal-confirm {
    width: 95%;
    margin: 10px;
  }
}

/* Ajustes para teléfonos pequeños */
@media screen and (max-width: 360px) {
  .table td {
    grid-template-columns: 100%;
  }

  .table td::before {
    margin-bottom: 4px;
  }

  .table td:last-child {
    justify-content: center;
  }

  .accion-btn {
    width: 30px;
    height: 30px;
    font-size: 12px;
  }

  .modal-header h2 {
    font-size: 1.2rem;
  }
}

/* Landscape mode */
@media screen and (max-height: 480px) and (orientation: landscape) {
  .modal-content {
    max-height: 100vh;
    margin: 0;
  }

  .modal-body {
    max-height: calc(100vh - 120px);
  }

  .form-group {
    margin-bottom: 8px;
  }

  .phone-input-container {
    flex-direction: row;
  }
}

/* Estilo para scroll personalizado */
.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #c09d62;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #a38655;
}

/* =======================================================
   Modo Oscuro
======================================================= */
/* Contenedor principal */
.dark .categorias-wrapper {
  background-color: #1e1e1e;
  color: #fff;
}

/* Inputs y búsqueda en modo oscuro */
.dark .busqueda {
  background-color: #2d2d2d;
  border-color: #404040;
  color: #fff;
}

.dark .custom-select {
  background-color: #2d2d2d;
  border-color: #404040;
  color: #fff;
}

.dark .custom-select option {
  background-color: #2d2d2d;
  color: #fff;
}

/* Tabla en modo oscuro */
.dark .table-container {
  background-color: #1e1e1e;
  border-color: #374151;
}

.dark .table {
  background-color: #1e1e1e;
}

.dark .table thead,
.dark .table thead th {
  background-color: #111827;
}

.dark .table th {
  background-color: #111827;
  color: #e5e7eb;
  border-bottom-color: #374151;
}

.dark .table td {
  color: #e5e7eb;
  border-bottom-color: #374151;
}

.dark .table tbody tr:hover {
  background-color: #1f2937;
}

/* Modal en modo oscuro */
.dark .modal-content {
  background-color: #2d2d2d;
  color: #fff;
}

.dark .modal-content input,
.dark .modal-content textarea {
  background-color: #383838;
  border-color: #404040;
  color: #fff;
}

.dark .form-group label {
  color: #fff;
}

/* Scroll personalizado en modo oscuro */
.dark .table-container::-webkit-scrollbar-track {
  background: #2d2d2d;
}

.dark .table-container::-webkit-scrollbar-thumb {
  background: #c09d62;
}

.dark .table-container::-webkit-scrollbar-thumb:hover {
  background: #a38655;
}

/* Botones en modo oscuro (manteniendo los colores originales) */
.dark .button-promocion {
  background-color: #4cafaf;
  color: white;
}

.dark .button-unidad-medida {
  background-color: #4caf4c;
  color: #000;
}

.dark #btnAdd {
  background-color: #c09d62;
  color: black;
}

.dark #btnAdd:hover {
  background-color: #a38655;
}


.dark .modalShowConfirm-Si {
  background-color: #dc3545;
  color: white;
}

.dark .modalShowConfirm-no {
  background-color: #6c757d;
  color: white;
}

/* Agregar estos estilos en la sección de estilos */
.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  margin-top: auto;
}

.pagination-info {
  color: #6c757d;
}

.pagination-container {
  display: flex;
  gap: 5px;
}

.pagination-button {
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  background-color: white;
  cursor: pointer;
  border-radius: 4px;
  min-width: 40px;
  transition: all 0.3s ease;
}

.pagination-button:hover:not(:disabled) {
  background-color: #e9ecef;
}

.pagination-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Agregar estos estilos al modo oscuro existente */
.dark .pagination-wrapper {
  border-color: #404040;
}

.dark .pagination-info {
  color: #aaa;
}

.dark .pagination-button {
  background-color: #2d2d2d;
  border-color: #404040;
  color: #fff;
}

.dark .pagination-button:hover:not(:disabled) {
  background-color: #383838;
}

/* Actualizar los Media Queries existentes */
@media screen and (max-width: 768px) {
  .pagination-wrapper {
    flex-direction: column;
    gap: 1rem;
  }

  .pagination-container {
    justify-content: center;
    flex-wrap: wrap;
  }
}

/* Estilos para la paginación */
.loading-indicator,
.no-data {
  text-align: center;
  padding: 2rem;
  font-size: 1.1rem;
  color: #666;
}
</style>
